import { Box, SelectList, Spacer, Text } from '@mariozechner/pi-tui';
import type { SelectItem } from '@mariozechner/pi-tui';

import type { ThinkingLevelSetting } from '../../onboarding/settings.js';
import { loadSettings, saveSettings } from '../../onboarding/settings.js';
import {
  THINKING_LEVELS,
  getThinkingLevelForModel,
  getThinkingLevelsForModel,
} from '../components/thinking-settings.js';
import { showModalOverlay } from '../overlay.js';
import { theme, getSelectListTheme } from '../theme.js';
import type { SlashCommandContext } from './types.js';

/** Models that support reasoning effort. */
function supportsThinking(modelId: string): boolean {
  return modelId.startsWith('openai/');
}

function getThinkingStatusLine(modelId: string, levelId: string): string {
  const level = getThinkingLevelForModel(modelId, levelId);
  return `Thinking: ${level.label}`;
}

function isThinkingLevelSetting(level: string): level is ThinkingLevelSetting {
  return THINKING_LEVELS.some(option => option.id === level);
}

function persistGlobalThinkingLevel(level: ThinkingLevelSetting): void {
  const settings = loadSettings();
  settings.preferences.thinkingLevel = level;
  saveSettings(settings);
}

function getModelNote(ctx: SlashCommandContext): string | null {
  const modelId = ctx.state.harness.getCurrentModelId() ?? '';
  if (!modelId) return 'No model selected.';
  if (!supportsThinking(modelId)) {
    return `Warning: current model (${modelId}) may not support reasoning effort. Setting will be saved but may not take effect.`;
  }
  return null;
}

export async function handleThinkCommand(ctx: SlashCommandContext, args: string[] = []): Promise<void> {
  const currentLevel = ((ctx.harness.getState() as any)?.thinkingLevel ?? 'off') as string;
  const modelId = ctx.state.harness.getCurrentModelId() ?? '';
  const thinkingLevels = getThinkingLevelsForModel(modelId);
  const arg = args[0]?.toLowerCase();

  if (arg === 'status') {
    ctx.showInfo(getThinkingStatusLine(modelId, currentLevel));
    return;
  }

  // Direct level argument: /think high
  if (arg) {
    const selected = thinkingLevels.find(l => l.id === arg);
    if (!selected) {
      ctx.showInfo(
        `Invalid thinking level: ${arg}. Use one of: ${THINKING_LEVELS.map(l => l.id).join(', ')} or 'status'.`,
      );
      return;
    }
    const note = getModelNote(ctx);
    await ctx.harness.setState({ thinkingLevel: selected.id } as any);
    persistGlobalThinkingLevel(selected.id);
    ctx.showInfo(getThinkingStatusLine(modelId, selected.id) + (note ? ` (${note})` : ''));
    return;
  }

  // No argument: show inline selector
  const items: SelectItem[] = thinkingLevels.map(l => ({
    value: l.id,
    label: `  ${l.label}  ${theme.fg('dim', l.description)}${l.id === currentLevel ? theme.fg('dim', ' (current)') : ''}`,
  }));

  const modelNote = getModelNote(ctx);

  return new Promise<void>(resolve => {
    const container = new Box(4, 2, text => theme.bg('overlayBg', text));
    container.addChild(new Text(theme.bold(theme.fg('accent', 'Thinking Level')), 0, 0));
    container.addChild(new Spacer(1));
    if (modelNote) {
      container.addChild(new Text(theme.fg('warning', modelNote), 0, 0));
      container.addChild(new Spacer(1));
    }

    const selectList = new SelectList(items, items.length, getSelectListTheme());

    selectList.onSelect = async (item: SelectItem) => {
      ctx.state.ui.hideOverlay();
      const selectedLevel = item.value;
      if (!isThinkingLevelSetting(selectedLevel)) {
        resolve();
        return;
      }

      try {
        await ctx.harness.setState({ thinkingLevel: selectedLevel } as any);
        persistGlobalThinkingLevel(selectedLevel);
        const selectedLabel = getThinkingLevelForModel(modelId, selectedLevel).label;
        ctx.showInfo(`Thinking → ${selectedLevel === currentLevel ? `${selectedLabel} (unchanged)` : selectedLabel}`);
      } catch {
        // Keep cancel behavior silent.
      } finally {
        resolve();
      }
    };

    selectList.onCancel = () => {
      ctx.state.ui.hideOverlay();
      resolve();
    };

    container.addChild(selectList);
    container.addChild(new Spacer(1));
    container.addChild(new Text(theme.fg('dim', '↑↓ navigate · Enter select · Esc cancel'), 0, 0));

    // Pre-select current level (after adding to container, matching models-pack pattern)
    const currentIdx = thinkingLevels.findIndex(l => l.id === currentLevel);
    if (currentIdx > 0) selectList.setSelectedIndex(currentIdx);

    const modal = container as Box & { handleInput: (data: string) => void };
    modal.handleInput = (data: string) => selectList.handleInput(data);
    showModalOverlay(ctx.state.ui, modal, { maxHeight: '60%' });
  });
}
